import React, { useCallback, useEffect } from "react";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import Table, { Column, DataSourceItem } from "../../Components/Atoms/Table";
import Tag from "../../Components/Atoms/Tag";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import { usePayoutStore } from "../../Store/Payout";
import { PAGINATION_KEYS, usePagination } from "../../Store/Pagination";
import { toLocalDateString } from "../../Utils/DateUtils";
import LoadingPage from "../LoadingPage";
import TablePaginationFooter from "../../Components/Atoms/Table/TablePaginationFooter";
import {
  AdyenBalanceAccountTransferStatus,
  Payout,
  PayoutChargebackScreenMessage
} from "../../Contracts/Transaction";
import { useTeamStore } from "../../Store/Team";
import { ReactComponent as ExclamationWarningIcon } from "../../Assets/ActionIcons/ExclamationWarningIcon.svg";
import { ReactComponent as InfoIcon } from "../../Assets/ActionIcons/InfoIcon.svg";
import { PaymentRoutes } from "../../Contracts/Routes";
import { useParams } from "react-router-dom";
import { PaymentsIntegrationEnum } from "../../Contracts/Integration";

const AdminTeamPayoutsListPage = () => {
  const { team_id } = useParams();
  const { limit, page, setLimit, setPage } = usePagination(PAGINATION_KEYS.ADMIN_TEAM_PAYOUT_LIST, {
    limit: 25,
    page: 1
  });
  const { formatCurrency } = useTeamStore();
  const {
    isLoading,
    payouts,
    getAdminPayoutList,
    connectedIntegration,
    payoutsCount,
    availableBalance,
    payoutScreenMessage
  } = usePayoutStore();
  const { getAccessTokenSilently } = useAuth0();

  const refreshThings = useCallback(async () => {
    if (team_id) {
      await getAdminPayoutList(getAccessTokenSilently, team_id, limit, "created_at:desc", page);
    }
  }, [getAccessTokenSilently, getAdminPayoutList, team_id, limit, page]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings, page, limit]);

  const dataSource: DataSourceItem<Payout>[] = payouts.map(payout => ({
    key: payout.id.toString(),
    date: toLocalDateString(payout.created_at),
    status: payout.status,
    amount: formatCurrency(payout.amount),
    reconcile: payout.is_reconcile_required,
    data: payout
  }));

  const columns: Column<Payout>[] = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "w-60",
      localSort: (a: DataSourceItem<Payout>, b: DataSourceItem<Payout>) =>
        a.date.localeCompare(b.date),
      render: d => (
        <div className="group flex items-center space-x-1">
          <div>{d.date}</div>
          {d.data.is_contain_chargeback && (
            <div className="relative">
              <InfoIcon className="w-3 h-3" />
              <div className="absolute text-xs left-full top-[-0.3rem] z-50 ml-2 px-2 py-1 bg-gray-950 text-white rounded-lg shadow-lg text-nowrap group-hover:block hidden">
                <div>Includes Chargeback Settlement</div>
                <svg
                  className="absolute left-[-8px] top-1/2 transform -translate-y-1/2"
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                >
                  <polygon points="0,4 8,0 8,8" />
                </svg>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      localSort: (a: DataSourceItem<Payout>, b: DataSourceItem<Payout>) =>
        a.amount < b.amount ? -1 : 1
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: item => (
        <Tag
          color={
            item.data.status === AdyenBalanceAccountTransferStatus.Failed ||
            item.data.status === AdyenBalanceAccountTransferStatus.Returned ||
            item.data.status === AdyenBalanceAccountTransferStatus.Refused
              ? "red"
              : item.data.status === AdyenBalanceAccountTransferStatus.Booked
                ? "lime"
                : "gray"
          }
          className="capitalize"
        >
          {item.data.status === AdyenBalanceAccountTransferStatus.Failed ||
          item.data.status === AdyenBalanceAccountTransferStatus.Returned ||
          item.data.status === AdyenBalanceAccountTransferStatus.Refused
            ? "Failed"
            : item.data.status === AdyenBalanceAccountTransferStatus.Booked
              ? "Paid out"
              : "Pending"}
        </Tag>
      )
    },
    {
      title: "Action",
      dataIndex: "reconcile_trouble_shoot",
      key: "reconcile_trouble_shoot",
      render: item => (
        <div className="flex items-center space-x-2">
          {item.data.is_reconcile_required &&
            connectedIntegration === PaymentsIntegrationEnum.Quickbooks && (
              <a
                href={PaymentRoutes.ADMIN_TEAM_PAYOUT_TROUBLESHOOT.replace(
                  ":payout_id",
                  item.data.id.toString()
                ).replace(":team_id", team_id || "")}
                className="text-blue-600"
              >
                Troubleshoot
              </a>
            )}
        </div>
      )
    }
  ];

  const constructInvoicesQuotesMessage = (
    message: PayoutChargebackScreenMessage
  ): React.ReactNode => {
    const { invoices, quotes } = message;

    const formatLinks = (items: { link: string; display_value: string }[]) => (
      <>
        {items.map(item => (
          <a
            href={item.link}
            key={item.link}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline"
          >
            {item.display_value}
            {", "}
          </a>
        ))}
      </>
    );

    if (invoices.length > 0 && quotes.length > 0) {
      return (
        <>
          Invoices: {formatLinks(invoices)} and Quotes: {formatLinks(quotes)}.
        </>
      );
    }
    if (invoices.length > 0) {
      return <>Invoices: {formatLinks(invoices)}.</>;
    }
    if (quotes.length > 0) {
      return <>Quotes: {formatLinks(quotes)}.</>;
    }
    return <></>;
  };

  return (
    <PaymentsAppLayout>
      <div className="flex flex-col h-[calc(100vh-9rem)]">
        {payoutScreenMessage?.refund_ref ? (
          <div className="flex w-full p-4 border rounded border-yellow-300 bg-yellow-50 space-x-2 mb-2">
            <ExclamationWarningIcon className="text-yellow-500 mt-1" />
            <div className="flex-grow text-gray-700">
              The balance is negative because a refund was issued for transaction number{" "}
              {payoutScreenMessage.refund_ref}. The account lacked sufficient funds, causing the
              negative balance, but it will become positive again once future invoices are collected
              and the refund amount is recovered.
            </div>
          </div>
        ) : (
          payoutScreenMessage?.pending_chargeback_transfers_count &&
          payoutScreenMessage?.pending_chargeback_transfers_count > 0 &&
          payoutScreenMessage?.chargeback_message && (
            <div className="flex w-full p-4 border rounded border-yellow-300 bg-yellow-50 space-x-2 mb-2">
              <ExclamationWarningIcon className="text-yellow-500 mt-1" />
              <span className="text-gray-700">
                {payoutScreenMessage.chargeback_message.is_payout_paused ? (
                  <>
                    Your payout is currently on hold because of chargebacks on{" "}
                    {constructInvoicesQuotesMessage(payoutScreenMessage.chargeback_message)} To
                    resume payouts, payouts will remain paused until the outstanding{" "}
                    {payoutScreenMessage.chargeback_message.out_standing_amount} balance is cleared.
                    Once your balance account has sufficient funds, payouts will automatically
                    resume.{" "}
                    <a
                      href={payoutScreenMessage.chargeback_message.article_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      Read More
                    </a>
                  </>
                ) : (
                  <>
                    <>The chargeback on</>{" "}
                    {constructInvoicesQuotesMessage(payoutScreenMessage.chargeback_message)}
                    <> resulted in an outstanding balance of</>
                    <>{payoutScreenMessage.chargeback_message.out_standing_amount} </>
                    <>
                      This amount will be automatically deducted from your upcoming payout to
                      resolve the balance.{" "}
                    </>
                    <a
                      href={payoutScreenMessage.chargeback_message.article_link}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600"
                    >
                      Read More
                    </a>
                  </>
                )}
              </span>
            </div>
          )
        )}
        {availableBalance !== undefined && (
          <div className="text-gray-700 m-1 sticky top-0">
            <span className="font-semibold">Available balance:</span>
            <span className="ml-2">{formatCurrency(availableBalance)}</span>
          </div>
        )}
        <div
          className={`flex flex-grow flex-col ${dataSource.length >= 14 ? "overflow-y-auto" : ""}`}
        >
          {isLoading && payouts.length === 0 ? (
            <LoadingPage />
          ) : (
            <Table dataSource={dataSource} columns={columns} />
          )}
          {!isLoading && payouts.length === 0 && (
            <div className="text-center text-gray-400 mt-12">No payouts available</div>
          )}
        </div>
        <TablePaginationFooter
          limit={limit}
          setLimit={setLimit}
          page={page}
          setPage={setPage}
          totalRecords={payoutsCount}
          isLoading={isLoading}
        />
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(AdminTeamPayoutsListPage, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
